import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { glassPopoverMenuClass } from '../../config/glassSurfaces';

const VIEWPORT_PAD = 8;
const ANCHOR_GAP = 8;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function computePosition(anchor, menu, preferredPlacement) {
  const menuWidth = menu.width;
  const menuHeight = menu.height;

  const placements = {
    'bottom-end': {
      top: anchor.bottom + ANCHOR_GAP,
      left: anchor.right - menuWidth,
    },
    'bottom-start': {
      top: anchor.bottom + ANCHOR_GAP,
      left: anchor.left,
    },
    'top-end': {
      top: anchor.top - menuHeight - ANCHOR_GAP,
      left: anchor.right - menuWidth,
    },
    'top-start': {
      top: anchor.top - menuHeight - ANCHOR_GAP,
      left: anchor.left,
    },
  };

  const flipMap = {
    'bottom-end': 'top-end',
    'bottom-start': 'top-start',
    'top-end': 'bottom-end',
    'top-start': 'bottom-start',
  };

  const fits = (placement) => {
    const pos = placements[placement];
    return (
      pos.top >= VIEWPORT_PAD &&
      pos.left >= VIEWPORT_PAD &&
      pos.top + menuHeight <= window.innerHeight - VIEWPORT_PAD &&
      pos.left + menuWidth <= window.innerWidth - VIEWPORT_PAD
    );
  };

  let placement = preferredPlacement;
  if (!fits(placement)) {
    const flipped = flipMap[placement];
    if (flipped && fits(flipped)) {
      placement = flipped;
    }
  }

  const pos = placements[placement];
  return {
    top: clamp(pos.top, VIEWPORT_PAD, window.innerHeight - menuHeight - VIEWPORT_PAD),
    left: clamp(pos.left, VIEWPORT_PAD, window.innerWidth - menuWidth - VIEWPORT_PAD),
  };
}

/**
 * Portaled frosted-glass popover — matches modal/header glass tokens.
 * Renders in document.body so backdrop-filter and positioning aren't clipped
 * by modal/card overflow containers.
 */
export default function PopoverMenu({
  open,
  onClose,
  anchorRef,
  placement = 'bottom-end',
  className = '',
  children,
}) {
  const menuRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current || !menuRef.current) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      const menu = menuRef.current?.getBoundingClientRect();
      if (!anchor || !menu) return;
      setPosition(computePosition(anchor, menu, placement));
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, anchorRef, placement, children]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (anchorRef?.current?.contains(event.target)) return;
      onClose?.();
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className={`fixed z-tooltip min-w-[10rem] ${glassPopoverMenuClass(className)}`}
      style={
        position
          ? { top: position.top, left: position.left }
          : { visibility: 'hidden', top: 0, left: 0 }
      }
    >
      {children}
    </div>,
    document.body,
  );
}

export function PopoverMenuItem({ children, className = '', ...props }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`flex w-full items-center px-4 py-3 text-left text-sm text-ink/80 transition-colors hover:bg-white/10 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
